import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Switch,
  Typography,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getAiModeChangeSettings, updateAiModeChangeSettings } from '../../services/api';

const AiModeChangeSettings = () => {
  const [settings, setSettings] = useState({
    enableAutomaticChanges: false,
    automaticContexts: [],
    requireApprovalContexts: [],
    disabledContexts: [],
    applyAutomaticOnCritical: true,
    notifyTherapistOnChange: true,
    logAllSuggestions: true,
    messageTemplates: {
      clientMessages: {
        automaticModeChange: "",
        therapistApprovedChange: ""
      },
      therapistMessages: {
        automaticModeChange: "",
        modeChangeSuggestion: ""
      },
      contextSpecificMessages: {
        crisis_situation: {
          client: "",
          therapist: ""
        },
        harmful_content: {
          client: "",
          therapist: ""
        },
        client_request_human: {
          client: "",
          therapist: ""
        }
      }
    }
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [expandedAccordion, setExpandedAccordion] = useState(null);
  
  // Define all available contexts with descriptions
  const availableContexts = [
    { 
      id: 'client_request_human', 
      label: 'Client requests human therapist', 
      description: 'When the client explicitly asks to speak with a human therapist'
    },
    { 
      id: 'crisis_situation', 
      label: 'Crisis situation', 
      description: 'When the client expresses thoughts of self-harm or is in acute distress'
    },
    { 
      id: 'complexity_beyond_ai', 
      label: 'Complex situation beyond AI capability', 
      description: 'When the client presents issues that require human expertise or clinical judgment'
    },
    { 
      id: 'harmful_content', 
      label: 'Harmful content detected', 
      description: 'When the client expresses intent to harm others or discusses illegal activities'
    },
    { 
      id: 'simple_query', 
      label: 'Simple informational query', 
      description: 'When the client is asking for basic information that the AI can handle'
    },
    { 
      id: 'routine_conversation', 
      label: 'Routine therapeutic conversation', 
      description: 'Normal therapeutic dialogue without escalation or special needs'
    }
  ];

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const data = await getAiModeChangeSettings(token);
      
      // Initialize message templates if they don't exist
      if (!data.messageTemplates) {
        data.messageTemplates = {
          clientMessages: {
            automaticModeChange: "AI automatically changed conversation mode to {targetMode} (Reason: {reason})",
            therapistApprovedChange: "Therapist approved AI suggestion to change conversation mode to {targetMode} (Reason: {reason})"
          },
          therapistMessages: {
            automaticModeChange: "AI automatically changed conversation mode to {targetMode} for client (Reason: {reason})",
            modeChangeSuggestion: "AI suggests changing mode to {targetMode} (Reason: {reason}, Urgency: {urgency})"
          },
          contextSpecificMessages: {
            crisis_situation: {
              client: "Due to concerning content, this conversation has been changed to HUMAN mode and your therapist has been notified.",
              therapist: "URGENT: Client expressed crisis content. Mode automatically changed to HUMAN."
            },
            harmful_content: {
              client: "Due to concerning content, this conversation has been changed to HUMAN mode and your therapist has been notified.",
              therapist: "Alert: Harmful content detected. Mode automatically changed to HUMAN."
            },
            client_request_human: {
              client: "Your request to speak with a human therapist has been received. The conversation mode has been changed to HUMAN.",
              therapist: "Client has requested to speak with a human therapist. Mode has been changed to HUMAN."
            }
          }
        };
      }
      
      setSettings(data);
    } catch (err) {
      console.error('Error fetching AI mode change settings:', err);
      console.error('Detailed error response:', err.response ? err.response.data : 'No response data');
      console.error('Error request:', err.request ? 'Request was made but no response' : 'No request made');
      console.error('Error config:', err.config);
      setError('Failed to load AI mode change settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    setError(null);
    setSaveSuccess(false);
    
    try {
      const token = localStorage.getItem('token');
      await updateAiModeChangeSettings(settings, token);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving AI mode change settings:', err);
      console.error('Detailed error response:', err.response ? err.response.data : 'No response data');
      console.error('Error request:', err.request ? 'Request was made but no response' : 'No request made');
      console.error('Error config:', err.config);
      console.error('Settings being saved:', JSON.stringify(settings, null, 2));
      setError('Failed to save AI mode change settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleMainSwitch = (event) => {
    setSettings({
      ...settings,
      enableAutomaticChanges: event.target.checked
    });
  };

  const handleToggleOption = (option) => (event) => {
    setSettings({
      ...settings,
      [option]: event.target.checked
    });
  };

  const handleContextChange = (contextId, targetList) => {
    // Remove context from all lists first
    const newSettings = {
      ...settings,
      automaticContexts: settings.automaticContexts.filter(id => id !== contextId),
      requireApprovalContexts: settings.requireApprovalContexts.filter(id => id !== contextId),
      disabledContexts: settings.disabledContexts.filter(id => id !== contextId)
    };
    
    // Add to target list if provided (otherwise it remains unassigned)
    if (targetList) {
      newSettings[targetList] = [...newSettings[targetList], contextId];
    }
    
    setSettings(newSettings);
  };

  // Handle message template changes
  const handleTemplateChange = (category, templateName, value) => {
    if (category === 'clientMessages' || category === 'therapistMessages') {
      setSettings({
        ...settings,
        messageTemplates: {
          ...settings.messageTemplates,
          [category]: {
            ...settings.messageTemplates[category],
            [templateName]: value
          }
        }
      });
    }
  };

  // Handle context-specific message changes
  const handleContextMessageChange = (contextId, userType, value) => {
    setSettings({
      ...settings,
      messageTemplates: {
        ...settings.messageTemplates,
        contextSpecificMessages: {
          ...settings.messageTemplates.contextSpecificMessages,
          [contextId]: {
            ...settings.messageTemplates.contextSpecificMessages[contextId],
            [userType]: value
          }
        }
      }
    });
  };

  // Handle accordion expansion
  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? panel : null);
  };

  // Find contexts that are not assigned to any list
  const getUnassignedContexts = () => {
    const allAssigned = [
      ...settings.automaticContexts,
      ...settings.requireApprovalContexts,
      ...settings.disabledContexts
    ];
    
    return availableContexts.filter(context => 
      !allAssigned.includes(context.id)
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card variant="outlined">
      <CardHeader 
        title="AI Mode Change Settings" 
        subheader="Configure when and how the AI should suggest or automatically change conversation modes"
      />
      <Divider />
      <CardContent>
        {error && (
          <Box mb={3}>
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          </Box>
        )}
        
        {saveSuccess && (
          <Box mb={3}>
            <Typography variant="body2" color="success">
              Settings saved successfully!
            </Typography>
          </Box>
        )}
        
        <Box mb={4}>
          <FormControl component="fieldset" fullWidth>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.enableAutomaticChanges}
                  onChange={handleToggleMainSwitch}
                  color="primary"
                />
              }
              label="Enable AI Mode Change Suggestions"
            />
            <FormHelperText>
              When enabled, the AI can suggest or automatically change conversation modes based on the settings below
            </FormHelperText>
          </FormControl>
        </Box>
        
        <Divider sx={{ mb: 3 }} />
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Typography variant="h6" gutterBottom>
              Automatic Mode Changes
              <Tooltip title="These contexts will trigger an immediate mode change without therapist approval">
                <InfoIcon fontSize="small" sx={{ ml: 1, verticalAlign: 'middle', color: 'info.main' }} />
              </Tooltip>
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'background.default' }}>
              <List dense>
                {settings.automaticContexts.length === 0 && (
                  <ListItem>
                    <ListItemText secondary="No automatic contexts selected" />
                  </ListItem>
                )}
                {settings.automaticContexts.map(contextId => {
                  const context = availableContexts.find(c => c.id === contextId);
                  return (
                    <ListItem 
                      key={contextId}
                      secondaryAction={
                        <Button 
                          size="small" 
                          onClick={() => handleContextChange(contextId, null)}
                        >
                          Remove
                        </Button>
                      }
                    >
                      <ListItemText 
                        primary={context?.label} 
                        secondary={context?.description}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Typography variant="h6" gutterBottom>
              Require Therapist Approval
              <Tooltip title="These contexts will send a suggestion to the therapist for approval before changing modes">
                <InfoIcon fontSize="small" sx={{ ml: 1, verticalAlign: 'middle', color: 'info.main' }} />
              </Tooltip>
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'background.default' }}>
              <List dense>
                {settings.requireApprovalContexts.length === 0 && (
                  <ListItem>
                    <ListItemText secondary="No approval contexts selected" />
                  </ListItem>
                )}
                {settings.requireApprovalContexts.map(contextId => {
                  const context = availableContexts.find(c => c.id === contextId);
                  return (
                    <ListItem 
                      key={contextId}
                      secondaryAction={
                        <Button 
                          size="small" 
                          onClick={() => handleContextChange(contextId, null)}
                        >
                          Remove
                        </Button>
                      }
                    >
                      <ListItemText 
                        primary={context?.label} 
                        secondary={context?.description}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Typography variant="h6" gutterBottom>
              Disabled Contexts
              <Tooltip title="The AI will not suggest mode changes for these contexts">
                <InfoIcon fontSize="small" sx={{ ml: 1, verticalAlign: 'middle', color: 'info.main' }} />
              </Tooltip>
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'background.default' }}>
              <List dense>
                {settings.disabledContexts.length === 0 && (
                  <ListItem>
                    <ListItemText secondary="No disabled contexts selected" />
                  </ListItem>
                )}
                {settings.disabledContexts.map(contextId => {
                  const context = availableContexts.find(c => c.id === contextId);
                  return (
                    <ListItem 
                      key={contextId}
                      secondaryAction={
                        <Button 
                          size="small" 
                          onClick={() => handleContextChange(contextId, null)}
                        >
                          Remove
                        </Button>
                      }
                    >
                      <ListItemText 
                        primary={context?.label} 
                        secondary={context?.description}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </Paper>
          </Grid>
        </Grid>
        
        <Box mt={4}>
          <Typography variant="h6" gutterBottom>
            Unassigned Contexts
            <Tooltip title="These contexts are not yet assigned to any category">
              <InfoIcon fontSize="small" sx={{ ml: 1, verticalAlign: 'middle', color: 'info.main' }} />
            </Tooltip>
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'background.default' }}>
            <List dense>
              {getUnassignedContexts().length === 0 && (
                <ListItem>
                  <ListItemText secondary="All contexts are assigned" />
                </ListItem>
              )}
              {getUnassignedContexts().map(context => (
                <ListItem 
                  key={context.id}
                  secondaryAction={
                    <Box>
                      <Button 
                        size="small" 
                        onClick={() => handleContextChange(context.id, 'automaticContexts')}
                        color="primary"
                        sx={{ mr: 1 }}
                      >
                        Automatic
                      </Button>
                      <Button 
                        size="small" 
                        onClick={() => handleContextChange(context.id, 'requireApprovalContexts')}
                        color="secondary"
                        sx={{ mr: 1 }}
                      >
                        Approval
                      </Button>
                      <Button 
                        size="small" 
                        onClick={() => handleContextChange(context.id, 'disabledContexts')}
                        color="error"
                      >
                        Disable
                      </Button>
                    </Box>
                  }
                >
                  <ListItemText 
                    primary={context.label} 
                    secondary={context.description}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Box>
        
        <Box mt={4}>
          <Typography variant="h6" gutterBottom>
            Additional Options
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'background.default' }}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={settings.applyAutomaticOnCritical}
                    onChange={handleToggleOption('applyAutomaticOnCritical')}
                    color="warning"
                  />
                }
                label={
                  <Box display="flex" alignItems="center">
                    <Typography variant="body1">Override settings for critical urgency</Typography>
                    <Tooltip title="When enabled, critical urgency situations will trigger automatic mode changes even for contexts that normally require approval or are disabled">
                      <WarningIcon fontSize="small" sx={{ ml: 1, color: 'warning.main' }} />
                    </Tooltip>
                  </Box>
                }
              />
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={settings.notifyTherapistOnChange}
                    onChange={handleToggleOption('notifyTherapistOnChange')}
                  />
                }
                label="Notify therapist when automatic mode changes occur"
              />
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={settings.logAllSuggestions}
                    onChange={handleToggleOption('logAllSuggestions')}
                  />
                }
                label="Log all AI mode change suggestions in activity log"
              />
            </FormGroup>
          </Paper>
        </Box>
        
        {/* New Message Templates Section */}
        <Box mt={4}>
          <Typography variant="h6" gutterBottom>
            Message Templates
            <Tooltip title="Customize the messages shown to clients and therapists when mode changes occur">
              <InfoIcon fontSize="small" sx={{ ml: 1, verticalAlign: 'middle', color: 'info.main' }} />
            </Tooltip>
          </Typography>
          
          {/* Default Templates Accordion */}
          <Accordion 
            expanded={expandedAccordion === 'defaultTemplates'} 
            onChange={handleAccordionChange('defaultTemplates')}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">Default Message Templates</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>Client Messages</Typography>
                  <TextField
                    fullWidth
                    label="Automatic Mode Change"
                    variant="outlined"
                    margin="normal"
                    value={settings.messageTemplates?.clientMessages?.automaticModeChange || ""}
                    onChange={(e) => handleTemplateChange('clientMessages', 'automaticModeChange', e.target.value)}
                    helperText="Available variables: {targetMode}, {reason}"
                  />
                  <TextField
                    fullWidth
                    label="Therapist Approved Change"
                    variant="outlined"
                    margin="normal"
                    value={settings.messageTemplates?.clientMessages?.therapistApprovedChange || ""}
                    onChange={(e) => handleTemplateChange('clientMessages', 'therapistApprovedChange', e.target.value)}
                    helperText="Available variables: {targetMode}, {reason}"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>Therapist Messages</Typography>
                  <TextField
                    fullWidth
                    label="Automatic Mode Change"
                    variant="outlined"
                    margin="normal"
                    value={settings.messageTemplates?.therapistMessages?.automaticModeChange || ""}
                    onChange={(e) => handleTemplateChange('therapistMessages', 'automaticModeChange', e.target.value)}
                    helperText="Available variables: {targetMode}, {reason}"
                  />
                  <TextField
                    fullWidth
                    label="Mode Change Suggestion"
                    variant="outlined"
                    margin="normal"
                    value={settings.messageTemplates?.therapistMessages?.modeChangeSuggestion || ""}
                    onChange={(e) => handleTemplateChange('therapistMessages', 'modeChangeSuggestion', e.target.value)}
                    helperText="Available variables: {targetMode}, {reason}, {urgency}"
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
          
          {/* Context-Specific Templates Accordions */}
          {settings.automaticContexts.map(contextId => {
            const context = availableContexts.find(c => c.id === contextId);
            if (!context) return null;
            
            return (
              <Accordion 
                key={contextId}
                expanded={expandedAccordion === `context_${contextId}`} 
                onChange={handleAccordionChange(`context_${contextId}`)}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">
                    {context.label} Messages
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Client Message"
                        variant="outlined"
                        margin="normal"
                        value={settings.messageTemplates?.contextSpecificMessages?.[contextId]?.client || ""}
                        onChange={(e) => handleContextMessageChange(contextId, 'client', e.target.value)}
                        helperText="Custom message shown to the client when this context is detected"
                      />
                      
                      <TextField
                        fullWidth
                        label="Therapist Message"
                        variant="outlined"
                        margin="normal"
                        value={settings.messageTemplates?.contextSpecificMessages?.[contextId]?.therapist || ""}
                        onChange={(e) => handleContextMessageChange(contextId, 'therapist', e.target.value)}
                        helperText="Custom message shown to the therapist when this context is detected"
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
        
        <Box mt={4} display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveSettings}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default AiModeChangeSettings;
